/**
 * MatcherBuilder — create/edit sheet for one need mapping (PIM Wave P4 Task
 * 7). Named for its most novel piece (the field-type-driven matcher-shape
 * picker) but, per the brief's file list, this single component owns the
 * WHOLE create/edit form: the mappable-field picker, the matcher inputs
 * (rendered by the picked field's type), the per-language phrase editor,
 * and the `slug`/`weight` fields. Self-contained `BottomSheet`, mirroring
 * `FieldFormDrawer`'s shell (own `open`/`onClose`, `useIsDesktop` side-panel
 * on desktop) rather than a separate wrapper file — `BottomSheet` mounts
 * nothing while closed (`packages/ui/src/BottomSheet.tsx`: "Mounts nothing
 * when closed and idle"), so the form below gets a fresh mount (fresh RHF
 * state) every time this sheet re-opens — no extra `key` trick is needed
 * the way `FieldFormDrawer` needs one for its multi-step wizard.
 *
 * Field-type -> matcher-kind (brief's rule, mirrors
 * `service._validate_matcher`'s own gate, `matcherKindForFieldType` in the
 * sibling `api.ts`): `size`/`number` -> `numeric_range` (min/max number
 * inputs, at least one required); `select` -> `option_set` (a checklist of
 * that field's OWN options, non-empty required). Every other field type has
 * no matcher shape and is excluded from the picker entirely.
 *
 * The field picker mirrors `BindingEditorPage`'s "NEW-rule pick list /
 * resolution superset" split: an archived field can still be the CURRENTLY
 * selected one (edit mode) — it is not silently dropped out of the
 * dropdown — but is never offered as a NEW choice.
 *
 * Phrases: three always-visible RU/KY/EN sections (not a `TrilingualInput`
 * language-switch tab) — each is a small tag editor (text input + Add +
 * removable chips) over a `string[]`, a shape `TrilingualInput` (one STRING
 * per language) cannot express. At least one phrase in any language is
 * required client-side (a mapping with zero phrases can never match
 * anything in the chatbot's need-extraction pipeline); the backend itself
 * accepts an empty `phrases` object with no shape validation beyond
 * `dict[str, list[str]]`, so this is a UI-only guardrail, not a mirrored
 * server rule.
 *
 * Server errors: the three matcher-validation codes
 * (`pim.need_mapping_matcher_invalid` / `_field_type_mismatch` /
 * `_option_unknown`) plus the two lifecycle codes
 * (`catalog_schema.need_mapping.duplicate_slug` / `_field_id_invalid`) are
 * mapped to the relevant form field via `setError`, mirroring
 * `FieldFormDrawer`'s `mapUnitCoherenceError`; any other `ApiError` (e.g. a
 * 404 from a mapping deleted out from under an open edit sheet) falls back
 * to a toast.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { BottomSheet, Button, Field } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import type { AttributeField, AttributeFieldOption } from "@/features/admin/attribute-fields/api";
import {
  useAllAttributeFields,
  useAttributeFieldOptions,
} from "@/features/admin/attribute-fields/hooks";
import { FIELD_SLUG_MAX_LEN, FIELD_SLUG_PATTERN } from "@/features/admin/attribute-fields/lib/slug";
import {
  matcherKindForFieldType,
  type NeedMapping,
  type NeedMappingCreate,
  type NeedMappingMatcher,
  type NeedMappingMatcherKind,
} from "@/features/admin/need-mappings/api";
import { useCreateNeedMapping, useUpdateNeedMapping } from "@/features/admin/need-mappings/hooks";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

/** Stable empty-array reference so a `useMemo` keyed on `fields` never
 * invalidates on its own while the drain query is still loading. */
const EMPTY_FIELDS: readonly AttributeField[] = [];

type PhraseLang = "ru" | "ky" | "en";
const PHRASE_LANGS: readonly PhraseLang[] = ["ru", "ky", "en"];

// ---------------------------------------------------------------------
// Form values + Zod schema
// ---------------------------------------------------------------------

interface NeedMappingFormValues {
  slug: string;
  field_id: string;
  weight: string;
  phrases: Record<PhraseLang, string[]>;
  numeric_min: string;
  numeric_max: string;
  option_slugs: string[];
}

/**
 * `matcherKind` drives the matcher-shape branch only — recomputed whenever
 * the selected field changes (`useMemo` keyed on it below). Passing a fresh
 * resolver value on every render (react-hook-form re-assigns `_options` on
 * each render pass) is the standard RHF pattern for a schema that depends on
 * live form state, so the matcher fields validate against whichever field is
 * CURRENTLY selected, not the one selected at mount.
 */
function buildSchema(matcherKind: NeedMappingMatcherKind | null) {
  return z
    .object({
      slug: z
        .string()
        .min(1, "formError.required")
        .max(FIELD_SLUG_MAX_LEN, "pim.fields.form.formError.slugPattern")
        .regex(FIELD_SLUG_PATTERN, "pim.fields.form.formError.slugPattern"),
      field_id: z.string().min(1, "formError.required"),
      weight: z.string(),
      phrases: z.object({
        ru: z.array(z.string()),
        ky: z.array(z.string()),
        en: z.array(z.string()),
      }),
      numeric_min: z.string(),
      numeric_max: z.string(),
      option_slugs: z.array(z.string()),
    })
    .superRefine((data, ctx) => {
      const totalPhrases = data.phrases.ru.length + data.phrases.ky.length + data.phrases.en.length;
      if (totalPhrases === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "pim.needMappings.form.formError.phrasesRequired",
          path: ["phrases", "ru"],
        });
      }

      const weightRaw = data.weight.trim();
      if (weightRaw !== "") {
        const n = Number(weightRaw.replace(",", "."));
        if (!Number.isFinite(n) || n < 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.numeric", path: ["weight"] });
        }
      }

      if (matcherKind === "numeric_range") {
        const minRaw = data.numeric_min.trim();
        const maxRaw = data.numeric_max.trim();
        if (minRaw === "" && maxRaw === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "pim.needMappings.form.formError.rangeRequired",
            path: ["numeric_min"],
          });
        }
        const minNum = minRaw === "" ? null : Number(minRaw.replace(",", "."));
        const maxNum = maxRaw === "" ? null : Number(maxRaw.replace(",", "."));
        if (minRaw !== "" && (minNum === null || !Number.isFinite(minNum))) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.numeric", path: ["numeric_min"] });
        }
        if (maxRaw !== "" && (maxNum === null || !Number.isFinite(maxNum))) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "formError.numeric", path: ["numeric_max"] });
        }
        if (
          minNum !== null &&
          maxNum !== null &&
          Number.isFinite(minNum) &&
          Number.isFinite(maxNum) &&
          minNum > maxNum
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "pim.needMappings.form.formError.rangeOrder",
            path: ["numeric_max"],
          });
        }
      } else if (matcherKind === "option_set") {
        if (data.option_slugs.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "pim.needMappings.form.formError.optionsRequired",
            path: ["option_slugs"],
          });
        }
      } else if (data.field_id !== "") {
        // Defensive only — the picker never offers a non-mappable field.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "pim.needMappings.form.formError.fieldNotMappable",
          path: ["field_id"],
        });
      }
    });
}

function defaultFormValues(mapping: NeedMapping | null): NeedMappingFormValues {
  if (!mapping) {
    return {
      slug: "",
      field_id: "",
      weight: "1",
      phrases: { ru: [], ky: [], en: [] },
      numeric_min: "",
      numeric_max: "",
      option_slugs: [],
    };
  }
  return {
    slug: mapping.slug,
    field_id: mapping.field_id,
    weight: mapping.weight,
    phrases: {
      ru: mapping.phrases["ru"] ?? [],
      ky: mapping.phrases["ky"] ?? [],
      en: mapping.phrases["en"] ?? [],
    },
    numeric_min:
      mapping.matcher.kind === "numeric_range" && mapping.matcher.min !== null
        ? String(mapping.matcher.min)
        : "",
    numeric_max:
      mapping.matcher.kind === "numeric_range" && mapping.matcher.max !== null
        ? String(mapping.matcher.max)
        : "",
    option_slugs: mapping.matcher.kind === "option_set" ? mapping.matcher.value_slugs : [],
  };
}

/** Maps the need-mapping domain-error codes onto the relevant form field,
 * mirroring `FieldFormDrawer.tsx`'s `mapUnitCoherenceError`. Any other
 * `ApiError` (e.g. a race against a concurrent delete) falls back to a
 * toast at the call site. */
function mapNeedMappingError(
  err: ApiError,
  matcherKind: NeedMappingMatcherKind | null,
): { field: keyof NeedMappingFormValues; messageKey: string } | null {
  switch (err.code) {
    case "catalog_schema.need_mapping.duplicate_slug":
      return { field: "slug", messageKey: "pim.needMappings.form.formError.slugDuplicate" };
    case "catalog_schema.need_mapping.field_id_invalid":
      return { field: "field_id", messageKey: "pim.needMappings.form.formError.fieldInvalid" };
    case "pim.need_mapping_field_type_mismatch":
      return { field: "field_id", messageKey: "pim.needMappings.form.formError.fieldTypeMismatch" };
    case "pim.need_mapping_option_unknown":
      return { field: "option_slugs", messageKey: "pim.needMappings.form.formError.optionUnknown" };
    case "pim.need_mapping_matcher_invalid":
      return {
        field: matcherKind === "option_set" ? "option_slugs" : "numeric_min",
        messageKey: "pim.needMappings.form.formError.matcherInvalid",
      };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------
// Per-language phrase tag editor
// ---------------------------------------------------------------------

interface PhraseLangEditorProps {
  lang: PhraseLang;
  values: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

function PhraseLangEditor({ lang, values, onChange, disabled = false }: PhraseLangEditorProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const [draft, setDraft] = useState("");

  const add = (): void => {
    const trimmed = draft.trim();
    if (trimmed === "" || values.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={`pim-need-mapping-phrase-${lang}`} className="text-[13px] font-semibold text-foreground">
        {t(`pim.needMappings.form.phrases.${lang}`)}
      </label>
      <div className="flex gap-2">
        <input
          id={`pim-need-mapping-phrase-${lang}`}
          data-testid={`pim-need-mapping-phrase-input-${lang}`}
          value={draft}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
          placeholder={t("pim.needMappings.form.phrases.placeholder")}
          className="min-h-[44px] flex-1 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)] disabled:cursor-not-allowed disabled:opacity-60"
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || draft.trim() === ""}
          onClick={add}
          data-testid={`pim-need-mapping-phrase-add-${lang}`}
          className="min-h-[44px]"
        >
          {t("pim.needMappings.form.phrases.add")}
        </Button>
      </div>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" data-testid={`pim-need-mapping-phrase-list-${lang}`}>
          {values.map((phrase, index) => (
            <span
              key={`${phrase}-${index}`}
              className="inline-flex items-center gap-1 rounded-full bg-[var(--g-elevated)] px-2.5 py-1 text-xs text-[var(--g-secondary)]"
            >
              {phrase}
              <button
                type="button"
                aria-label={t("pim.needMappings.form.phrases.remove", { phrase })}
                data-testid={`pim-need-mapping-phrase-remove-${lang}-${index}`}
                disabled={disabled}
                onClick={() => onChange(values.filter((_, i) => i !== index))}
                className="ml-0.5 text-[var(--g-secondary)] hover:text-[var(--g-error)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:cursor-not-allowed"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Matcher-kind-specific inputs
// ---------------------------------------------------------------------

interface NumericRangeInputsProps {
  minValue: string;
  maxValue: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  minError?: string;
  maxError?: string;
}

function NumericRangeInputs({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  minError,
  maxError,
}: NumericRangeInputsProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field
        label={t("pim.needMappings.form.numericMin")}
        data-testid="pim-need-mapping-numeric-min"
        type="number"
        inputMode="decimal"
        value={minValue}
        onChange={(event) => onMinChange(event.target.value)}
        error={minError}
      />
      <Field
        label={t("pim.needMappings.form.numericMax")}
        data-testid="pim-need-mapping-numeric-max"
        type="number"
        inputMode="decimal"
        value={maxValue}
        onChange={(event) => onMaxChange(event.target.value)}
        error={maxError}
      />
    </div>
  );
}

interface OptionSetInputsProps {
  fieldId: string;
  selected: string[];
  onChange: (next: string[]) => void;
  error?: string;
}

function OptionSetInputs({ fieldId, selected, onChange, error }: OptionSetInputsProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const { data, isLoading, isError } = useAttributeFieldOptions(fieldId);
  const options: AttributeFieldOption[] = useMemo(
    () => (data?.options ?? []).filter((o) => !o.is_archived).slice().sort((a, b) => a.sort_order - b.sort_order),
    [data],
  );

  const toggle = (slug: string): void => {
    onChange(selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] font-semibold text-foreground">{t("pim.needMappings.form.optionsLabel")}</span>
      {isLoading ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("pim.options.loading")}</p>
      ) : isError ? (
        <p className="text-sm text-[var(--g-error)]">{t("pim.options.loadError")}</p>
      ) : options.length === 0 ? (
        <p className="text-sm text-[var(--g-secondary)]">{t("pim.needMappings.form.optionsEmpty")}</p>
      ) : (
        <div
          role="group"
          aria-label={t("pim.needMappings.form.optionsLabel")}
          data-testid="pim-need-mapping-options"
          className="flex flex-col gap-1.5 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] p-2"
        >
          {options.map((option) => (
            <label
              key={option.id}
              className="flex min-h-[44px] items-center gap-2 rounded-[var(--g-radius-sm)] px-2 py-1.5 text-sm hover:bg-[var(--g-elevated)]"
            >
              <input
                type="checkbox"
                data-testid={`pim-need-mapping-option-${option.value_slug}`}
                checked={selected.includes(option.value_slug)}
                onChange={() => toggle(option.value_slug)}
                className="size-5 accent-[var(--g-primary)]"
              />
              <span>{option.label_ru}</span>
              <span className="font-mono text-xs text-[var(--g-secondary)]">{option.value_slug}</span>
            </label>
          ))}
        </div>
      )}
      {error ? (
        <p role="alert" className="text-xs text-[var(--g-error)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------

interface MatcherBuilderFormProps {
  mapping: NeedMapping | null;
  onDone: () => void;
}

function MatcherBuilderForm({ mapping, onDone }: MatcherBuilderFormProps): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  const fieldsQuery = useAllAttributeFields();
  const fields = fieldsQuery.data ?? EMPTY_FIELDS;
  const createMutation = useCreateNeedMapping();
  const updateMutation = useUpdateNeedMapping();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // `field_id` is tracked as plain local state (not routed through RHF's
  // `watch`) because the Zod schema itself depends on the selected field's
  // matcher kind — the resolver has to exist BEFORE `useForm` is called, so
  // it cannot depend on anything `useForm` returns (`watch` included).
  // Mirrors how `numeric_min`/`numeric_max`/`option_slugs` below are already
  // fully controlled rather than `register`ed.
  const [fieldId, setFieldId] = useState<string>(mapping?.field_id ?? "");
  const selectedField = fields.find((f) => f.id === fieldId) ?? null;
  const matcherKind = selectedField ? matcherKindForFieldType(selectedField.field_type) : null;

  const schema = useMemo(() => buildSchema(matcherKind), [matcherKind]);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<NeedMappingFormValues>({
    resolver,
    defaultValues: defaultFormValues(mapping),
  });

  const phrases = watch("phrases");
  const optionSlugs = watch("option_slugs");
  const numericMin = watch("numeric_min");
  const numericMax = watch("numeric_max");

  /** Fires ONLY on a user-driven field pick (never on the async field
   * catalogue simply arriving post-mount) — resets the matcher-specific
   * values when the newly-picked field's matcher kind actually differs from
   * the previous one, so switching e.g. a `size` field for another `size`
   * field keeps the admin's numeric bounds, but switching to a `select`
   * field clears them (and vice versa). Edit-mode's own initial values (from
   * `defaultFormValues`) are never touched by this — it only runs from the
   * `<select>`'s `onChange` below. */
  const handleFieldChange = (nextFieldId: string): void => {
    const nextField = fields.find((f) => f.id === nextFieldId) ?? null;
    const nextKind = nextField ? matcherKindForFieldType(nextField.field_type) : null;
    setFieldId(nextFieldId);
    setValue("field_id", nextFieldId, { shouldValidate: true });
    if (nextKind !== matcherKind) {
      setValue("numeric_min", "");
      setValue("numeric_max", "");
      setValue("option_slugs", []);
    }
  };

  /** NEW-rule pick list — non-archived + mappable types only, EXCEPT the
   * currently-selected field (edit mode) which must still resolve even if
   * archived (`BindingEditorPage`'s resolution-superset rule). */
  const pickableFields: AttributeField[] = useMemo(
    () =>
      fields
        .filter((f) => matcherKindForFieldType(f.field_type) !== null)
        .filter((f) => !f.is_archived || f.id === mapping?.field_id)
        .slice()
        .sort((a, b) => a.name_ru.localeCompare(b.name_ru, "ru")),
    [fields, mapping],
  );

  const errText = (key?: string, params?: Record<string, unknown>): string | undefined =>
    key ? t(key, params) : undefined;

  const submit = (values: NeedMappingFormValues): void => {
    if (!selectedField || matcherKind === null) return;

    let matcher: NeedMappingMatcher;
    if (matcherKind === "numeric_range") {
      const minRaw = values.numeric_min.trim();
      const maxRaw = values.numeric_max.trim();
      matcher = {
        kind: "numeric_range",
        min: minRaw === "" ? null : Number(minRaw.replace(",", ".")),
        max: maxRaw === "" ? null : Number(maxRaw.replace(",", ".")),
      };
    } else {
      matcher = { kind: "option_set", value_slugs: values.option_slugs };
    }

    const phraseBody: Record<string, string[]> = {};
    for (const lang of PHRASE_LANGS) {
      if (values.phrases[lang].length > 0) phraseBody[lang] = values.phrases[lang];
    }

    const weight = values.weight.trim() === "" ? "1" : values.weight.trim();

    const onWriteError = (err: unknown): void => {
      if (err instanceof ApiError) {
        const mapped = mapNeedMappingError(err, matcherKind);
        if (mapped) {
          setError(mapped.field, { type: "server", message: mapped.messageKey });
          return;
        }
      }
      toast.error(t("error.generic"));
    };

    if (!mapping) {
      const body: NeedMappingCreate = {
        slug: values.slug,
        field_id: values.field_id,
        phrases: phraseBody,
        matcher,
        weight,
      };
      createMutation.mutate(body, {
        onSuccess: () => {
          toast.success(t("pim.needMappings.form.createSuccess"));
          onDone();
        },
        onError: onWriteError,
      });
    } else {
      updateMutation.mutate(
        {
          id: mapping.id,
          body: {
            slug: values.slug,
            field_id: values.field_id,
            phrases: phraseBody,
            matcher,
            weight,
          },
        },
        {
          onSuccess: () => {
            toast.success(t("pim.needMappings.form.updateSuccess"));
            onDone();
          },
          onError: onWriteError,
        },
      );
    }
  };

  const numericMinError = errText(errors.numeric_min?.message);
  const numericMaxError = errText(errors.numeric_max?.message);
  const optionsError = errText(errors.option_slugs?.message);
  const phrasesError = errText(errors.phrases?.ru?.message);

  return (
    <form data-testid="pim-need-mapping-form" onSubmit={handleSubmit(submit)} className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <Field
          label={t("pim.needMappings.form.slugLabel")}
          data-testid="pim-need-mapping-slug"
          hint={t("pim.needMappings.form.slugHint")}
          error={errText(errors.slug?.message)}
          {...register("slug")}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="pim-need-mapping-field" className="text-[13px] font-semibold text-foreground">
            {t("pim.needMappings.form.fieldLabel")}
          </label>
          <select
            id="pim-need-mapping-field"
            data-testid="pim-need-mapping-field"
            className={SELECT_CLASS}
            value={fieldId}
            onChange={(event) => handleFieldChange(event.target.value)}
          >
            <option value="">{t("pim.needMappings.form.fieldPlaceholder")}</option>
            {pickableFields.map((field) => (
              <option key={field.id} value={field.id}>
                {field.name_ru}
                {field.is_archived ? ` (${t("pim.fields.status.archived")})` : ""}
              </option>
            ))}
          </select>
          {errors.field_id?.message ? (
            <p role="alert" className="text-xs text-[var(--g-error)]">
              {errText(errors.field_id.message)}
            </p>
          ) : null}
        </div>
      </section>

      {matcherKind === "numeric_range" ? (
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
            {t("pim.needMappings.form.matcherSection")}
          </h3>
          <NumericRangeInputs
            minValue={numericMin}
            maxValue={numericMax}
            onMinChange={(v) => setValue("numeric_min", v)}
            onMaxChange={(v) => setValue("numeric_max", v)}
            minError={numericMinError}
            maxError={numericMaxError}
          />
        </section>
      ) : matcherKind === "option_set" ? (
        <section className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
            {t("pim.needMappings.form.matcherSection")}
          </h3>
          <OptionSetInputs
            fieldId={fieldId}
            selected={optionSlugs}
            onChange={(next) => setValue("option_slugs", next)}
            error={optionsError}
          />
        </section>
      ) : (
        <p className="text-sm text-[var(--g-secondary)]">{t("pim.needMappings.form.noFieldSelectedHint")}</p>
      )}

      <section className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
          {t("pim.needMappings.form.phrasesSection")}
        </h3>
        {PHRASE_LANGS.map((lang) => (
          <PhraseLangEditor
            key={lang}
            lang={lang}
            values={phrases[lang]}
            onChange={(next) => setValue("phrases", { ...phrases, [lang]: next })}
          />
        ))}
        {phrasesError ? (
          <p role="alert" className="text-xs text-[var(--g-error)]">
            {phrasesError}
          </p>
        ) : null}
      </section>

      <Field
        label={t("pim.needMappings.form.weightLabel")}
        data-testid="pim-need-mapping-weight"
        type="number"
        min={0}
        step={0.1}
        inputMode="decimal"
        hint={t("pim.needMappings.form.weightHint")}
        error={errText(errors.weight?.message)}
        {...register("weight")}
      />

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone} disabled={isPending} className="min-h-[44px]">
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          data-testid="pim-need-mapping-submit"
          loading={isPending}
          className="min-h-[44px]"
        >
          {mapping ? t("pim.needMappings.form.submitUpdate") : t("pim.needMappings.form.submitCreate")}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// Sheet shell
// ---------------------------------------------------------------------

export interface MatcherBuilderProps {
  open: boolean;
  /** `null` = create mode; a loaded mapping = edit mode. */
  mapping: NeedMapping | null;
  onClose: () => void;
}

export function MatcherBuilder({ open, mapping, onClose }: MatcherBuilderProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const title = mapping ? t("pim.needMappings.form.editTitle") : t("pim.needMappings.form.createTitle");

  return (
    <BottomSheet open={open} onClose={onClose} title={title} desktop={isDesktop}>
      <MatcherBuilderForm mapping={mapping} onDone={onClose} />
    </BottomSheet>
  );
}

export default MatcherBuilder;
